import React, { useState } from 'react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import FullScreenVideo from '@components/shared/FullScreenVideo';
import { PageSlice, SliceVmsMicrobiomeVideo } from '@contracts';
import PlayImage from '@images/play.svg';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';
import LazyLoadVideo from '../LazyLoadVideo';

interface Props {
  enabled: boolean;
  content?: SliceVmsMicrobiomeVideo;
  slice?: PageSlice;
}

interface VideoAttributes {
  youtubeId?: string;
  videoId?: string;
  width: number;
  height: number;
  visible: boolean;
}

const FALLBACK_FULLSCREEN_VIDEO_WIDTH = 1024;
const FALLBACK_FULLSCREEN_VIDEO_HEIGHT = 768;

const FULLSCREEN_YOUTUBE_VIDEO_WIDTH = 1120;
const FULLSCREEN_YOUTUBE_VIDEO_HEIGHT = 630;

const getVideoAttributes = (data: SliceVmsMicrobiomeVideo, showFullscreenVideo: boolean) => {
  const isYoutubeVideo = Boolean(data?.youtube_video_id?.text);
  let videoAttributes: VideoAttributes;

  if (isYoutubeVideo) {
    videoAttributes = {
      youtubeId: data.youtube_video_id?.text,
      width: FULLSCREEN_YOUTUBE_VIDEO_WIDTH,
      height: FULLSCREEN_YOUTUBE_VIDEO_HEIGHT,
      visible: showFullscreenVideo
    };
  } else {
    videoAttributes = {
      videoId: data?.microbiome_video?.url,
      width: Number(data?.microbiome_video_width) || FALLBACK_FULLSCREEN_VIDEO_WIDTH,
      height: Number(data?.microbiome_video_height) || FALLBACK_FULLSCREEN_VIDEO_HEIGHT,
      visible: showFullscreenVideo
    };
  }

  return videoAttributes;
};

const MicrobiomeVideo: React.FC<Props> = (properties) => {
  const [showFullscreenVideo, setShowFullscreenVideo] = useState<boolean>(false);
  const { content, slice } = properties;
  const data = slice ? slice.primary : content;
  const titleHtml = useReplacedInnerHtml(data?.microbiome_title?.text);
  const textHtml = useReplacedInnerHtml(data?.microbiome_text?.text);
  const videoAttributes = getVideoAttributes(data, showFullscreenVideo);

  return (
    <>
      <MicrobiomeFrame>
        <MicrobiomeText>
          <h3 dangerouslySetInnerHTML={{ __html: titleHtml }} />
          <p dangerouslySetInnerHTML={{ __html: textHtml }} />
          <MicrobiomeVideoDiv display={properties.enabled ? 'flex' : 'none'} onClick={() => setShowFullscreenVideo(true)}>
            <PlayButton>
              <PlayIcon />
            </PlayButton>
            <span>{data?.microbiome_more.text}</span>
            <StyledLazyLoadVideo>
              <video
                width="170px"
                height="108px"
                autoPlay={true}
                preload="none"
                loop={true}
                muted={true}
                playsInline={true}
                onClick={() => setShowFullscreenVideo(true)}
              >
                <source src={data?.microbiome_video.url} type="video/mp4" />
              </video>
            </StyledLazyLoadVideo>
          </MicrobiomeVideoDiv>
        </MicrobiomeText>
        <img width="434px" height="434px" src={data?.microbiome_image.url} alt="vaginal flora illustration" />
      </MicrobiomeFrame>
      <FullScreenVideo
        youtubeId={videoAttributes.youtubeId}
        videoId={videoAttributes.videoId}
        width={videoAttributes.width}
        height={videoAttributes.height}
        visible={videoAttributes.visible}
        setVisible={setShowFullscreenVideo}
      />
    </>
  );
};

export default MicrobiomeVideo;

const MicrobiomeFrame = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0;
  gap: 40px;
  flex: none;
  flex-grow: 0;

  @media only screen and (max-width: 1060px) {
    gap: 0;
    flex-direction: column;

    img {
      width: 200px;
      height: 245px;
    }
  }
`;

const MicrobiomeText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0;
  gap: 40px;
  max-width: 476px;
  flex: none;
  order: 0;
  flex-grow: 0;

  @media only screen and (max-width: 1060px) {
    gap: 20px;
    max-width: 315px;
    text-align: center;
    align-items: center;
    order: 3;
  }

  h3 {
    font-family: 'Domaine';
    font-style: normal;
    font-weight: normal;
    font-size: 30px;
    line-height: 120%;
    display: flex;
    align-items: flex-end;
    color: ${Colors.navyBlue};
    flex: none;
    order: 0;
    align-self: stretch;
    flex-grow: 0;

    @media only screen and (max-width: 1060px) {
      font-size: 24px;
      text-align: center;
      padding: 0 30px 0 30px;
      letter-spacing: -0.02em;
    }
  }

  p {
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 400;
    font-size: 18px;
    line-height: 150%;
    color: ${Colors.navyBlue};
    flex: none;
    order: 1;
    align-self: stretch;
    flex-grow: 0;

    @media only screen and (max-width: 1060px) {
      font-size: 16px;
    }
  }
`;

const MicrobiomeVideoDiv = styled.div<{ display: string }>`
  box-sizing: border-box;
  display: ${(p) => p.display};
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0;
  gap: 10px;
  position: relative;
  width: 400px;
  background: #fcf2de;
  border: 1px solid ${Colors.ecru};
  border-radius: 3px;
  flex: none;
  order: 1;
  flex-grow: 0;
  cursor: pointer;

  & > div {
    display: flex;
    justify-content: center;
    align-items: center;

    & > video {
      max-width: 100%;
      max-height: 100%;
    }
  }

  @media only screen and (max-width: 1060px) {
    width: 310px;
  }

  span {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 30px 0 30px 40px;
    gap: 20px;
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 700;
    font-size: 16px;
    line-height: 150%;
    max-width: 182px;
    color: ${Colors.navyBlue};
    flex: none;
    order: 0;
    flex-grow: 0;
    @media only screen and (max-width: 1060px) {
      padding: 18px 30px 18px 30px;
      font-size: 13px;
    }
  }

  video {
    cursor: pointer;
    @media only screen and (max-width: 1060px) {
      width: 120px;
      height: 76px;
    }
  }
`;

const PlayButton = styled.div`
  box-sizing: border-box;
  position: absolute;
  right: 131px;
  top: 68px;
  cursor: pointer;
  @media only screen and (max-width: 1060px) {
    right: 85px;
    top: 40px;
  }

  img {
    transform: scale(0.5);
  }
`;
const PlayIcon = styled(PlayImage)`
  transform: scale(0.5);
  @media only screen and (max-width: 1060px) {
    transform: scale(0.3);
  }
`;

const StyledLazyLoadVideo = styled(LazyLoadVideo)`
  background: rgba(0, 0, 0, 0.5);
`;
